import { ExternalLink } from "lucide-react";

/**
 * A whole-pumpkin-shaped Amazon buy button. The pumpkin body is the button
 * itself — not a rectangle with a pumpkin icon.
 *
 * Composition:
 *  - Green stem + leaf on top
 *  - Three overlapping orange ellipses form the classic pumpkin body
 *  - Ridge lines across the segments
 *  - "Amazon" label baked into the SVG, centered
 *  - Playful hover: gentle wiggle + lift
 */
export default function PumpkinButton({ href, label = "Amazon" }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Buy on ${label}`}
      className="pumpkin-btn"
      data-testid="book-buy-amazon"
    >
      <svg
        viewBox="0 0 220 170"
        width="200"
        height="155"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="pumpkinBody" cx="45%" cy="40%" r="65%">
            <stop offset="0%" stopColor="#FFB273" />
            <stop offset="55%" stopColor="#FF8C42" />
            <stop offset="100%" stopColor="#E7601C" />
          </radialGradient>
          <radialGradient id="pumpkinLobe" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#FF9A55" />
            <stop offset="100%" stopColor="#E36817" />
          </radialGradient>
          <linearGradient id="pumpkinStem" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4E9C57" />
            <stop offset="100%" stopColor="#2E7A3A" />
          </linearGradient>
          <filter id="pumpkinShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#B34412" floodOpacity="0.42" />
          </filter>
        </defs>

        {/* Curly vine */}
        <path
          d="M 138 34 Q 154 26 162 34 Q 168 42 158 46"
          stroke="#3F8A44"
          strokeWidth="2.2"
          strokeLinecap="round"
          fill="none"
        />

        {/* Green stem */}
        <path
          d="M 100 24 Q 96 34 100 44 Q 110 48 120 44 Q 124 34 120 24 Q 110 18 100 24 Z"
          fill="url(#pumpkinStem)"
        />
        <path
          d="M 106 26 Q 110 34 106 42"
          stroke="#2E5F2E"
          strokeWidth="1.4"
          fill="none"
          strokeLinecap="round"
        />

        {/* Leaf */}
        <path
          d="M 122 34 Q 138 24 148 30 Q 144 42 132 42 Q 124 40 122 34 Z"
          fill="#4E9C57"
        />
        <path
          d="M 124 34 Q 138 32 146 32"
          stroke="#2E5F2E"
          strokeWidth="1"
          fill="none"
          strokeLinecap="round"
        />

        {/* Pumpkin body — 5 overlapping lobes for depth */}
        <g filter="url(#pumpkinShadow)">
          {/* Outer lobes (darker) */}
          <ellipse cx="52"  cy="108" rx="40" ry="55" fill="url(#pumpkinLobe)" />
          <ellipse cx="168" cy="108" rx="40" ry="55" fill="url(#pumpkinLobe)" />
          {/* Middle lobes */}
          <ellipse cx="78"  cy="108" rx="38" ry="58" fill="#FF8C42" />
          <ellipse cx="142" cy="108" rx="38" ry="58" fill="#FF8C42" />
          {/* Center front lobe (biggest, lightest) */}
          <ellipse cx="110" cy="110" rx="46" ry="60" fill="url(#pumpkinBody)" />
        </g>

        {/* Segment ridges */}
        <path d="M 80 54 Q 66 108 80 162" stroke="#B34412" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.55" />
        <path d="M 140 54 Q 154 108 140 162" stroke="#B34412" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.55" />
        <path d="M 96 50 Q 92 108 96 166" stroke="#B34412" strokeWidth="0.9" fill="none" strokeLinecap="round" opacity="0.35" />
        <path d="M 124 50 Q 128 108 124 166" stroke="#B34412" strokeWidth="0.9" fill="none" strokeLinecap="round" opacity="0.35" />

        {/* Highlight blush */}
        <ellipse cx="88" cy="82" rx="10" ry="18" fill="#FFD5A6" opacity="0.55" />
        <ellipse cx="82" cy="76" rx="4" ry="7" fill="#FFF3E0" opacity="0.75" />

        {/* Label — carved into the pumpkin like a jack-o'-lantern glow */}
        <text
          x="110"
          y="118"
          textAnchor="middle"
          fontFamily="'Fredoka', sans-serif"
          fontSize="28"
          fontWeight="700"
          fill="#FFFFFF"
          style={{ paintOrder: "stroke", stroke: "#B34412", strokeWidth: 1.2 }}
        >
          {label}
        </text>
        <text
          x="110"
          y="140"
          textAnchor="middle"
          fontFamily="'Fredoka', sans-serif"
          fontSize="12"
          fontWeight="600"
          fill="#FFFFFF"
          opacity="0.94"
        >
          grab yours &rarr;
        </text>
      </svg>
      <span className="pumpkin-btn-external" aria-hidden="true">
        <ExternalLink size={12} />
      </span>
    </a>
  );
}
